import {
  useClientByIdQuery,
  useCountryAllWarehouseListQuery,
  useShippingAddressLastCheckQuery,
  useShippingAddressQuery,
} from 'api';
import { useParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useCountryMapping } from 'hooks/useCountryMapping';
import { FormattedShippingInfo, ShippingInfo } from 'typings/client';

export const useShippingAddresses = () => {
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [shippingAddressId, setShippingAddressId] = useState<string | undefined>(undefined);

  const { id } = useParams() as { id: string };
  const { countryMapping } = useCountryMapping();
  const { data: clientDetails = {} } = useClientByIdQuery({ id });
  const { data: { data: shippingData = [] } = {} } = useShippingAddressQuery({ id });
  const { data: warehouseData = [] } = useCountryAllWarehouseListQuery();
  const { data, isFetching } = useShippingAddressLastCheckQuery(
    { id: shippingAddressId as string },
    { skip: !shippingAddressId } // Skip query if `shippingAddressId` is null or undefined
  );

  const formattedShippingData = useMemo(
    () =>
      shippingData.map((_data: ShippingInfo): FormattedShippingInfo => {
        const country = countryMapping[_data.countryId];
        const warehouse = warehouseData.find((_warehouse: Record<string, any>) => _warehouse.id === _data.warehouseId);
        const fullAddress = [country?.commonName, _data.streetAddress, _data.city, _data.state, _data.zip]
          .filter(Boolean)
          .join(', ');
        return {
          warehouseName: warehouse?.name,
          fullAddress,
          ..._data,
        };
      }),
    [shippingData, countryMapping, warehouseData]
  );

  const onAddModalStateChange = useCallback(() => {
    setAddModalState((prevState) => !prevState);
  }, []);

  const onDeleteModalOpen = useCallback((id: string) => {
    setShippingAddressId(id);
  }, []);

  const onDeleteModalClose = useCallback(() => {
    setShippingAddressId(undefined);
  }, []);

  return {
    addModalState,
    shippingAddressId,
    clientDetails,
    formattedShippingData,
    onAddModalStateChange,
    onDeleteModalOpen,
    onDeleteModalClose,
    isFetching,
    isLastAddress: data?.data,
  };
};
